import React, { useState } from "react";
import {
  Phone,
  MapPin,
  Star,
  Clock,
  Send,
  ArrowRight,
  Facebook,
  ShieldCheck,
  CheckCircle2,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { businessInfo } from "../data/mockData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    projectType: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data.detail ||
            "Something went wrong sending your enquiry. Please call us on " +
              businessInfo.phone
        );
      }
      setSubmitted(true);
      setFormData({
        name: "",
        phone: "",
        email: "",
        projectType: "",
        message: "",
      });
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(
    businessInfo.address
  )}&output=embed`;

  return (
    <section
      id="contact"
      className="relative py-28 bg-gradient-to-b from-white via-gray-50/60 to-white overflow-hidden"
      data-testid="contact-section"
    >
      {/* Subtle background pattern */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(31,62,90,1) 1px, transparent 1px), linear-gradient(90deg, rgba(31,62,90,1) 1px, transparent 1px)",
          backgroundSize: "52px 52px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-brand-mint-dark text-xs font-bold uppercase tracking-[0.25em] mb-4">
            Contact Us
          </p>
          <h2 className="font-heading text-3xl lg:text-4xl text-gray-900 mb-4">
            Get Your Free Quote Today
          </h2>
          <p className="text-gray-500 text-[15px] max-w-xl mx-auto leading-relaxed">
            Tell us about your space — we'll arrange a free site survey and
            send a no-obligation written quote within days.
          </p>
        </div>

        {/* Main grid */}
        <div className="grid lg:grid-cols-5 gap-6 lg:gap-8 items-stretch">
          {/* LEFT — Dark info panel */}
          <aside
            className="lg:col-span-2 relative rounded-2xl overflow-hidden bg-gradient-to-br from-brand-charcoal-dark via-brand-charcoal to-[#264676] text-white p-8 sm:p-10 flex flex-col"
            data-testid="contact-info-panel"
          >
            {/* Decorative blurs */}
            <div
              aria-hidden="true"
              className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand-gold/10 blur-3xl"
            />
            <div
              aria-hidden="true"
              className="absolute -bottom-32 -left-24 w-72 h-72 rounded-full bg-brand-mint/10 blur-3xl"
            />
            {/* Grid texture */}
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-[0.06] pointer-events-none"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
                backgroundSize: "36px 36px",
              }}
            />

            <div className="relative flex flex-col h-full">
              {/* Top: rating */}
              <div className="mb-9">
                <div className="flex items-center gap-1.5 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 text-brand-gold fill-brand-gold"
                    />
                  ))}
                  <span className="ml-2 text-[11px] text-white/70 font-bold uppercase tracking-[0.18em]">
                    {businessInfo.rating.toFixed(1)} · {businessInfo.reviewCount}{" "}
                    Google Reviews
                  </span>
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl leading-tight">
                  Yorkshire's trusted AC specialist.
                </h3>
                <p className="text-white/65 text-sm leading-relaxed mt-3 max-w-md">
                  F-Gas &amp; NVQ3 qualified refrigeration and air conditioning
                  engineer, and a member of REFCOM. Friendly, clean installs
                  from one-room splits to multi-floor commercial fit-outs.
                </p>
              </div>

              {/* Contact details */}
              <div className="space-y-5 mb-8">
                <a
                  href={`tel:${businessInfo.phone}`}
                  className="group flex items-start gap-4"
                  data-testid="contact-phone-link"
                >
                  <span className="w-11 h-11 rounded-xl bg-white/10 border border-white/10 group-hover:bg-brand-gold group-hover:border-brand-gold transition-colors flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-white group-hover:text-brand-charcoal-dark transition-colors" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-white/55 mb-1">
                      Call us
                    </span>
                    <span className="block font-heading text-lg leading-tight group-hover:text-brand-gold transition-colors">
                      {businessInfo.phone}
                    </span>
                    <span className="block text-white/55 text-xs mt-1">
                      Tap to call — replies within hours
                    </span>
                  </span>
                </a>

                <div className="flex items-start gap-4">
                  <span className="w-11 h-11 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-white" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-white/55 mb-1">
                      Based in
                    </span>
                    <span className="block text-white text-sm font-medium leading-snug">
                      {businessInfo.address}
                    </span>
                    <span className="block text-white/55 text-xs mt-1">
                      Serving {businessInfo.area}
                    </span>
                  </span>
                </div>

                {businessInfo.hours && (
                  <div className="flex items-start gap-4">
                    <span className="w-11 h-11 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4 text-white" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-white/55 mb-1">
                        Opening hours
                      </span>
                      <span className="block text-white text-sm font-medium leading-snug">
                        {businessInfo.hours.weekdays}
                      </span>
                      <span className="block text-white/65 text-xs leading-snug mt-0.5">
                        {businessInfo.hours.saturday} · {businessInfo.hours.sunday}
                      </span>
                      <span className="block text-white/55 text-xs mt-1">
                        Emergency call-outs available outside hours
                      </span>
                    </span>
                  </div>
                )}
              </div>

              {/* Embedded Map */}
              <div className="mt-auto rounded-xl overflow-hidden border border-white/10 shadow-2xl">
                <iframe
                  src={mapSrc}
                  title={`Map of ${businessInfo.name}`}
                  className="w-full h-44 sm:h-48 grayscale-[0.3] contrast-[1.05] block"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              {/* Socials row */}
              {businessInfo.facebook && (
                <div className="mt-5 flex items-center justify-between gap-4 pt-5 border-t border-white/10">
                  <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/55">
                    Follow us
                  </span>
                  <a
                    href={businessInfo.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-white/10 hover:bg-brand-gold hover:text-brand-charcoal-dark text-white flex items-center justify-center transition-colors"
                    aria-label="Facebook"
                    data-testid="contact-facebook"
                  >
                    <Facebook className="w-4 h-4" />
                  </a>
                </div>
              )}
            </div>
          </aside>

          {/* RIGHT — Form card */}
          <div className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              className="relative bg-white rounded-2xl p-7 sm:p-9 border border-gray-100 shadow-[0_18px_50px_-20px_rgba(31,62,90,0.18)]"
              data-testid="contact-form"
            >
              {/* Trust strip */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pb-6 mb-6 border-b border-gray-100">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-brand-charcoal">
                  <ShieldCheck className="w-3.5 h-3.5 text-brand-mint-dark" />
                  F-Gas &amp; NVQ3 Qualified
                </span>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-brand-charcoal">
                  <CheckCircle2 className="w-3.5 h-3.5 text-brand-mint-dark" />
                  Free No-Obligation Quote
                </span>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-brand-charcoal">
                  <Star className="w-3.5 h-3.5 text-brand-gold-dark fill-brand-gold-dark" />
                  {businessInfo.rating.toFixed(1)} Google
                </span>
              </div>

              {submitted && (
                <div
                  className="mb-5 p-4 bg-green-50 border border-green-100 rounded-lg text-green-700 text-sm font-medium flex items-center gap-2"
                  data-testid="contact-success"
                >
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                  Thanks — we've got your enquiry and we'll be in touch shortly.
                </div>
              )}

              {error && (
                <div
                  className="mb-5 p-4 bg-red-50 border border-red-100 rounded-lg text-red-700 text-sm font-medium flex items-center gap-2"
                  data-testid="contact-error"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <FormField label="Your Name" required>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Smith"
                    className={fieldClasses}
                    data-testid="contact-name"
                  />
                </FormField>
                <FormField label="Phone Number" required>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="07xxx xxxxxx"
                    className={fieldClasses}
                    data-testid="contact-phone-input"
                  />
                </FormField>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <FormField label="Email Address" required>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                    className={fieldClasses}
                    data-testid="contact-email"
                  />
                </FormField>
                <FormField label="Service Required" required>
                  <div className="relative">
                    <select
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      required
                      className={`${fieldClasses} appearance-none pr-10 cursor-pointer`}
                      data-testid="contact-project-type"
                    >
                      <option value="">Select a service</option>
                      <option value="air-handling-units">
                        Air Handling Units
                      </option>
                      <option value="cellar-cooling">Cellar Cooling</option>
                      <option value="ventilation-ducting">
                        Ventilation &amp; Ducting
                      </option>
                      <option value="industrial">Industrial</option>
                      <option value="modular">Modular</option>
                      <option value="maintenance">
                        Maintenance &amp; Service
                      </option>
                      <option value="commercial">Commercial Install</option>
                      <option value="residential">Residential Install</option>
                      <option value="other">Other / Free Survey</option>
                    </select>
                    <ArrowRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 rotate-90 pointer-events-none" />
                  </div>
                </FormField>
              </div>

              <FormField label="Tell us about your project">
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Rooms / area to cool, building type, timeline, anything else useful…"
                  className={`${fieldClasses} resize-none`}
                  data-testid="contact-message"
                />
              </FormField>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-6 pt-5 border-t border-gray-100">
                <button
                  type="submit"
                  disabled={submitting}
                  className="group inline-flex items-center justify-center gap-2 bg-brand-charcoal hover:bg-brand-charcoal-dark text-white px-7 py-3.5 rounded-lg text-sm font-bold tracking-wide transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                      Send Enquiry
                    </>
                  )}
                </button>
                <a
                  href={`tel:${businessInfo.phone}`}
                  className="inline-flex items-center justify-center gap-2 border border-gray-200 hover:border-brand-charcoal text-brand-charcoal hover:bg-gray-50 px-7 py-3.5 rounded-lg text-sm font-bold tracking-wide transition-all duration-200"
                  data-testid="contact-call-cta"
                >
                  <Phone className="w-4 h-4" />
                  Or call {businessInfo.phone}
                </a>
              </div>
              <p className="mt-4 text-[11px] text-gray-400 leading-relaxed">
                We typically reply within a few hours during opening hours.
                Your details are only used to respond to this enquiry.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const fieldClasses =
  "w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-mint/40 focus:border-brand-mint transition-all";

const FormField = ({ label, required, children }) => (
  <div>
    <label className="flex items-center gap-1 text-[11px] font-bold text-gray-500 mb-1.5 uppercase tracking-[0.12em]">
      {label}
      {required && <span className="text-brand-gold-dark">*</span>}
    </label>
    {children}
  </div>
);

export default Contact;
